import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

export function NewConversationModal({ currentUser, onClose, onCreated }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [kind, setKind] = useState('individual'); // individual | location | all | custom
  const [recipients, setRecipients] = useState([]); // for picker
  const [picked, setPicked] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const isMgrPlus = window.EmpireRoles.isManager(currentUser);

  useEffect(() => {
    api('GET', '/api/chat/recipients', null, headers).then(r => setRecipients(r.users || [])).catch(() => {});
    api('GET', '/api/locations').then(setLocations).catch(() => {});
  }, []);

  function togglePick(id) {
    setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  }

  async function start() {
    let audience = null;
    if (kind === 'individual') {
      if (picked.length !== 1) { setErr('Pick exactly one person.'); return; }
      audience = { kind: 'individual', user_ids: picked };
    } else if (kind === 'location') {
      if (!locationId) { setErr('Pick a location.'); return; }
      audience = { kind: 'location', location_id: locationId };
    } else if (kind === 'all') {
      audience = { kind: 'all' };
    } else if (kind === 'custom') {
      if (picked.length === 0) { setErr('Pick at least one person.'); return; }
      audience = { kind: 'custom', user_ids: picked };
    }
    setBusy(true); setErr('');
    try {
      const r = await api('POST', '/api/chat/conversations', { audience }, headers);
      onCreated(r.conversation);
    } catch (e) {
      let m = e.message; try { m = JSON.parse(e.message).error; } catch {}
      setErr(m);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl flex flex-col fade-in shadow-2xl"
           style={{ maxHeight: '85vh' }}
           onClick={e => e.stopPropagation()}>
        <div className="bg-ink rounded-t-2xl p-5 flex items-center justify-between flex-shrink-0">
          <h3 className="text-cream font-bold text-base">New message</h3>
          <button onClick={onClose} className="text-cream/50 hover:text-cream text-3xl leading-none">×</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Send to</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {[
                { id: 'individual', label: 'Individual' },
                { id: 'location',   label: 'Location group' },
                ...(isMgrPlus ? [{ id: 'all', label: 'All staff' }] : []),
                { id: 'custom',     label: 'Custom group' },
              ].map(opt => (
                <button key={opt.id} onClick={() => { setKind(opt.id); setPicked([]); setLocationId(null); setErr(''); }}
                  style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                            borderRadius: 6,
                            border: `1px solid ${kind === opt.id ? 'var(--forest)' : '#d0d4d9'}`,
                            background: kind === opt.id ? 'var(--forest)' : '#fff',
                            color: kind === opt.id ? '#fff' : '#37474f', cursor: 'pointer' }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {(kind === 'individual' || kind === 'custom') && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                {kind === 'individual' ? 'Pick one person' : 'Pick people'}
              </div>
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or role…"
                style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: '1px solid #d0d4d9',
                          borderRadius: 6, marginBottom: 6, fontFamily: 'inherit' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '40vh', overflowY: 'auto' }}>
                {recipients.length === 0 && (
                  <div style={{ color: '#9e9e9e', fontSize: 12 }}>No coworkers found.</div>
                )}
                {recipients
                  .filter((u) => {
                    const q = search.trim().toLowerCase();
                    if (!q) return true;
                    return (u.name || '').toLowerCase().includes(q)
                        || (u.role || '').toLowerCase().includes(q);
                  })
                  .map(u => {
                  const on = picked.includes(u.id);
                  return (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                                                  border: `1px solid ${on ? 'var(--forest)' : '#e0e0e0'}`, borderRadius: 6,
                                                  background: on ? 'rgba(45,122,58,0.08)' : '#fff', cursor: 'pointer' }}>
                      <input type={kind === 'individual' ? 'radio' : 'checkbox'}
                        checked={on}
                        onChange={() => kind === 'individual' ? setPicked([u.id]) : togglePick(u.id)} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#212121' }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: '#9e9e9e' }}>{u.role}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          {kind === 'location' && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Pick a location</div>
              <select value={locationId || ''} onChange={e => setLocationId(Number(e.target.value) || null)}
                style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d0d4d9', borderRadius: 6 }}>
                <option value="">— Choose one —</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}
          {kind === 'all' && (
            <div style={{ fontSize: 13, color: '#757575' }}>
              This will message every active staff member. Use sparingly.
            </div>
          )}
          {err && <div style={{ background: '#fee', padding: 8, borderRadius: 6, color: '#b91c1c', fontSize: 13 }}>{err}</div>}
        </div>
        <div className="p-4 flex gap-3 flex-shrink-0 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm">
            Cancel
          </button>
          <button onClick={start} disabled={busy}
            className="flex-1 py-3 rounded-xl bg-crimson text-white font-semibold text-sm disabled:opacity-60">
            {busy ? 'Working…' : 'Start'}
          </button>
        </div>
      </div>
    </div>
  );
}
